import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { EVENT_TYPE_META, EVENT_TYPE_ORDER, formatDayLabel, isoDay, localBucketDay } from '../lib/historyMeta';
import { getPriorityLabel } from '../lib/priority';
import { requestOutcomeLabel } from '../lib/historyList';
import { WorkOrderPrintButton } from './WorkOrderPrintButton';

/**
 * Month calendar showing event chips per day. Click a day → side panel.
 *
 * Props:
 *   - events: [{ date, event_type, title, equipment_name, machinery_id, raw_id, ... }, ...]
 *   - onEventClick: (event) => void  // typically routes to /machinery/:id
 *   - initialMonth?: 'YYYY-MM'       // defaults to current month
 *   - oldestDate?: 'YYYY-MM-DD'      // for prev/next bounds (heatmap can override)
 *   - newestDate?: 'YYYY-MM-DD'
 */
const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function monthMatrix(year, month0) {
  // Returns 6 rows × 7 cols of Date objects covering the month grid (leading + trailing).
  const first = new Date(year, month0, 1);
  const start = new Date(first);
  start.setDate(start.getDate() - first.getDay()); // back to Sunday
  const rows = [];
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) {
      const d = new Date(start);
      d.setDate(start.getDate() + r * 7 + c);
      row.push(d);
    }
    rows.push(row);
  }
  return rows;
}

const MaintenanceCalendar = ({ events, onEventClick, initialMonth }) => {
  const today = new Date();
  const initial = (() => {
    if (initialMonth) {
      const [y, m] = initialMonth.split('-').map(Number);
      return { year: y, month0: m - 1 };
    }
    return { year: today.getFullYear(), month0: today.getMonth() };
  })();
  const [cursor, setCursor] = useState(initial);
  const [selectedDay, setSelectedDay] = useState(null);

  // When the parent re-feeds `initialMonth` (e.g. heatmap click), follow it.
  useEffect(() => {
    if (!initialMonth) return;
    const [y, m] = initialMonth.split('-').map(Number);
    setCursor({ year: y, month0: m - 1 });
  }, [initialMonth]);

  // Index events by LOCAL day for O(1) lookup while rendering. Reads
  // `event_date` (raw ISO) when available so a log timestamped
  // `2026-08-11T02:00:00Z` is bucketed under Aug 10 in Eastern Time, matching
  // the List view's `toLocaleDateString()` output.
  const byDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const day = localBucketDay(e);
      if (!day) return;
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(e);
    });
    return m;
  }, [events]);

  const matrix = useMemo(
    () => monthMatrix(cursor.year, cursor.month0),
    [cursor.year, cursor.month0]
  );
  const monthLabel = new Date(cursor.year, cursor.month0, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const handlePrev = () => {
    setCursor((c) =>
      c.month0 === 0 ? { year: c.year - 1, month0: 11 } : { year: c.year, month0: c.month0 - 1 }
    );
  };
  const handleNext = () => {
    setCursor((c) =>
      c.month0 === 11 ? { year: c.year + 1, month0: 0 } : { year: c.year, month0: c.month0 + 1 }
    );
  };
  const handleToday = () => {
    const now = new Date();
    setCursor({ year: now.getFullYear(), month0: now.getMonth() });
    setSelectedDay(isoDay(now));
  };

  const selectedEvents = selectedDay ? byDay.get(selectedDay) || [] : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6">
      {/* Calendar grid */}
      <div className="min-w-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrev}
              data-testid="cal-prev-month-btn"
            >
              ←
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              data-testid="cal-today-btn"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              data-testid="cal-next-month-btn"
            >
              →
            </Button>
            <div className="ml-2 text-lg font-semibold" data-testid="cal-month-label">
              {monthLabel}
            </div>
          </div>

          {/* Color legend */}
          <div className="hidden md:flex items-center gap-3 text-xs text-slate-600">
            {EVENT_TYPE_ORDER.map((t) => (
              <div key={t} className="flex items-center gap-1">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${EVENT_TYPE_META[t].chipClass}`} />
                <span>{EVENT_TYPE_META[t].short}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-7 text-xs font-medium text-slate-500 mb-1">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="px-2 py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {matrix.flat().map((d) => {
            const iso = isoDay(d);
            const inMonth = d.getMonth() === cursor.month0;
            const isToday = isoDay(today) === iso;
            const isSelected = selectedDay === iso;
            const dayEvents = byDay.get(iso) || [];
            const visibleChips = dayEvents.slice(0, 3);
            const overflow = dayEvents.length - visibleChips.length;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelectedDay(iso)}
                data-testid={`cal-day-${iso}`}
                className={`relative min-h-[88px] text-left border rounded-md p-2 transition hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400 ${
                  inMonth ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 text-slate-400'
                } ${isSelected ? 'ring-2 ring-slate-700' : ''} ${
                  isToday ? 'border-slate-900' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className={`text-xs font-semibold ${isToday ? 'text-slate-900' : ''}`}>
                    {d.getDate()}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="text-[10px] text-slate-500">{dayEvents.length}</span>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {visibleChips.map((e) => {
                    const meta = EVENT_TYPE_META[e.event_type] || EVENT_TYPE_META.maintenance_log;
                    return (
                      <div
                        key={e.id}
                        className={`text-[10px] truncate rounded px-1 py-[1px] border ${meta.cellClass}`}
                        title={`${meta.label}: ${e.title}`}
                      >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle ${meta.chipClass}`} />
                        {e.title}
                      </div>
                    );
                  })}
                  {overflow > 0 && (
                    <div className="text-[10px] text-slate-500">+{overflow} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Side panel */}
      <aside
        className="border rounded-lg bg-white p-4 h-fit sticky top-4"
        data-testid="cal-side-panel"
      >
        {!selectedDay ? (
          <div className="text-sm text-slate-500">
            Pick a day on the calendar to see what happened on it.
          </div>
        ) : (
          <>
            <div className="text-sm font-semibold mb-1" data-testid="cal-side-day-label">
              {formatDayLabel(selectedDay)}
            </div>
            {selectedEvents.length === 0 ? (
              <div className="text-sm text-slate-500">No maintenance recorded on this day.</div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {selectedEvents.map((e) => {
                  const meta = EVENT_TYPE_META[e.event_type] || EVENT_TYPE_META.maintenance_log;
                  return (
                    <div key={e.id} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => onEventClick && onEventClick(e)}
                      className={`w-full min-w-0 flex-1 text-left border rounded-md p-3 hover:shadow-sm transition ${meta.cellClass}`}
                      data-testid={`cal-side-event-${e.id}`}
                    >
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-75">
                        <span className={`inline-block h-2 w-2 rounded-full ${meta.chipClass}`} />
                        {requestOutcomeLabel(e) || meta.label}
                      </div>
                      <div className="font-semibold mt-1 leading-tight break-words">{e.title}</div>
                      <div className="text-xs mt-1 opacity-80">{e.equipment_name}</div>
                      {e.priority && (
                        <div className="text-xs mt-1 opacity-80">Priority: {getPriorityLabel(e.priority)}</div>
                      )}
                    </button>
                    {['open_work_order', 'completed_work_order'].includes(e.event_type) && e.raw_id && <WorkOrderPrintButton workOrderId={e.raw_id} compact testId={`calendar-print-wo-btn-${e.raw_id}`} />}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
};

export default MaintenanceCalendar;
